import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  alpha,
  Card,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useChains } from '../contexts/ChainContext';


const NoHotelSelectedCampaign = ({
  hotels,
  filteredHotels,
  recentlyViewedHotels,
  handleHeroIconClick,
  clearRecentlyViewed,
  setSelectedHotel,
  setFilters,
  setFilteredHotels,
  sidebarCollapsed = false,
  searchQuery = '',
  onSearchChange,
  onHotelSelect,
  categories = []
}) => {
  // Get chains from context - they're already ordered by displayOrder from the context
  const { getChainLogoUrl } = useChains();
  
  // Typing animation for placeholder
  const [typingText, setTypingText] = useState('');
  const [currentHotelIndex, setCurrentHotelIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Search functionality
  const [showTypingAnimation, setShowTypingAnimation] = useState(true);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const selectedItemRef = useRef(null);
  
  const hotelNames = useMemo(() => [
    'Hobo Helsinki',
    'Hotel Fabian',
    'Clarion Hotel Umeå',
    'Comfort Hotel Umeå City',
    'Clarion Hotel Aviapolis',
    'Comfort Hotel Jazz',
    'Quality Hotel Sveitsi',
    'Comfort Hotel LT',
    'Comfort Hotel Skellefteå',
    'Hotel Føroyar',
    'Clarion Hotel Wisby',
    'Clarion Hotel Stockholm',
    'Comfort Hotel Norrköping',
    'Quality Hotel Friends',
    'Clarion Hotel Sign',
  ], []);
  
  useEffect(() => {
    // Only run typing animation when search is empty and animation should show
    if (!showTypingAnimation || searchQuery.length > 0) return;
    
    const currentHotel = hotelNames[currentHotelIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (typingText.length < currentHotel.length) {
          setTypingText(currentHotel.slice(0, typingText.length + 1));
        } else {
          // Finished typing, wait then start deleting
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        // Deleting
        if (typingText.length > 0) {
          setTypingText(typingText.slice(0, -1));
        } else {
          // Finished deleting, move to next hotel
          setIsDeleting(false);
          setCurrentHotelIndex((prev) => (prev + 1) % hotelNames.length);
        }
      }
    }, isDeleting ? 50 : 100); // Faster deletion, slower typing
    
    return () => clearTimeout(timeout);
  }, [typingText, currentHotelIndex, isDeleting, hotelNames, showTypingAnimation, searchQuery]);
  
  // Filter hotels based on search query - matching the main search logic
  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    
    // Use the same normalization function as the main search
    const normalizeScandinavianCharacters = (str) => {
      if (!str) return '';
      return str
        .toLowerCase()
        .replace(/[ä]/g, 'a')
        .replace(/[ö]/g, 'o')
        .replace(/[å]/g, 'a');
    };
    
    const getNormalizedString = (str) => {
      if (!str) return '';
      return normalizeScandinavianCharacters(str);
    };
    
    const searchLower = getNormalizedString(searchQuery.trim());
    
    return filteredHotels.filter(hotel => {
      // Search ONLY in Hotel Name and Property Number
      
      // Check Property Number (try multiple potential locations)
      const propertyNumber = 
        hotel.propertyNumber || 
        hotel.categoryData?.category_propertynumber?.value || 
        hotel.id;

      if (propertyNumber && getNormalizedString(String(propertyNumber)).includes(searchLower)) {
        return true;
      }

      // Check Hotel Name (try multiple potential locations)
      const hotelName = 
        hotel.name || 
        hotel.categoryData?.category_name?.value || 
        hotel.categoryData?.category_hotelname?.value;

      if (hotelName && getNormalizedString(String(hotelName)).includes(searchLower)) {
        return true;
      }
      
      return false;
    }).slice(0, 8); // Limit to 8 results
  }, [searchQuery, filteredHotels]);

  // Reset selected index when search results change
  useEffect(() => {
    setSelectedResultIndex(-1);
  }, [searchResults]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current && selectedResultIndex >= 0) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [selectedResultIndex]);

  // Handle search input - use the same pattern as HotelList
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setShowTypingAnimation(value.length === 0);
    setShowSearchResults(value.length >= 2);
    setSelectedResultIndex(-1); // Reset selection when typing
    
    // Use the same onSearchChange handler as the main search
    if (onSearchChange && typeof onSearchChange === 'function') {
      onSearchChange(event);
    }
  };

  // Handle keyboard navigation in search results
  const handleKeyDown = (event) => {
    if (!showSearchResults || searchResults.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedResultIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedResultIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedResultIndex >= 0 && selectedResultIndex < searchResults.length) {
          handleHotelSelect(searchResults[selectedResultIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setShowSearchResults(false);
        setSelectedResultIndex(-1);
        break;
      default:
        // Do nothing for other keys
        break;
    }
  };

  // Handle hotel selection from search results
  const handleHotelSelect = (hotel) => {
    if (onHotelSelect) {
      onHotelSelect(hotel);
    }
    setShowSearchResults(false);
  };
  
  
  
  
  return (
    <Box 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3, lg: 4 }, // Padding around the card
        textAlign: 'center',
        position: 'relative',
        boxSizing: 'border-box',
        overflow: 'hidden',
        width: '100%',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: 'translateZ(0)',
      }}
    >
      {/* Main Card Container - Campaign Style with Rounded Corners */}
      <Card
        elevation={0}
        sx={{
          width: '100%',
          height: '100%', // Take up full height
          borderRadius: 4, // Rounded corners like original
          position: 'relative',
          overflow: 'hidden',
          // Match navigation background styling
          bgcolor: 'background.paper',
          boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.03)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            boxShadow: theme => `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
          },
        }}
      >
        {/* Main Content Container - Campaign Structure */}
        <Box sx={{ 
          width: '100%',
          height: '100%', // Take up full height of card
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'center', lg: 'stretch' },
          gap: { xs: 4, lg: 8 },
          textAlign: { xs: 'center', lg: 'left' },
          p: { xs: 2.5, sm: 3, lg: 3.5 }, // Padding inside the card
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: 'translateZ(0)',
        }}>
        
         {/* Left Column - Text Content (Campaign Style) */}
         <Box sx={{ 
           flex: '0 0 40%', // 40% width
           display: 'flex',
           flexDirection: 'column',
           justifyContent: 'center',
           alignItems: 'center', // Center text horizontally
           textAlign: 'center', // Center text alignment
           // Campaign-inspired spacing using brand guidelines
           p: { xs: 2, sm: 3 }, // 3x spacing
           transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
         }}>
          
          {/* Overline/Category - Campaign Style */}
          <Typography 
            variant="overline" 
            sx={{ 
              fontFamily: 'StrawberrySansText',
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.15em',
              color: theme => theme.palette.mode === 'dark' ? 'white' : 'black',
              mb: 3, // More spacing like campaign
              display: 'block',
              textTransform: 'uppercase'
            }}
          >
            HOTEL MANAGER
          </Typography>

          {/* Main Heading - Campaign Style */}
          <Typography 
            variant="h1" 
            sx={{ 
              fontFamily: 'StrawberrySansDisplay',
              fontWeight: 400,
              fontSize: { xs: '2.5rem', sm: '3rem', lg: '3.5rem' },
              lineHeight: 1.1,
              color: theme => theme.palette.mode === 'dark' ? 'white' : 'black',
              mb: 4, // More spacing like campaign
              letterSpacing: '-0.02em'
            }}
          >
            Your complete
            <Box component="span" sx={{ 
              display: 'block',
              color: theme => theme.palette.mode === 'dark' ? 'white' : 'black',
              fontWeight: 400, // Same font weight as first line
              fontSize: { xs: '2.5rem', sm: '3rem', lg: '3.5rem' } // Same font size as first line
            }}>
              hotel overview
            </Box>
          </Typography>
          
          {/* Subtitle - Tool-focused */}
          <Typography 
            variant="h5" 
            sx={{ 
              fontFamily: 'StrawberrySansText',
              fontWeight: 500,
              fontSize: { xs: '1.2rem', sm: '1.4rem' },
              lineHeight: 1.4,
              color: theme => theme.palette.mode === 'dark' ? 'white' : 'black',
              mb: 4, // More spacing like campaign
              maxWidth: 480
            }}
          >
            Search and explore hotel information
          </Typography>

          {/* Description - Tool-focused */}
          <Typography 
            variant="body1" 
            sx={{ 
              fontFamily: 'StrawberrySansText',
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              lineHeight: 1.7,
              color: theme => theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.7)' 
                : 'rgba(0, 0, 0, 0.7)',
              mb: { xs: 3, lg: 4 },
              maxWidth: 460
            }}
          >
            Find hotels by name, location, or any property details. View comprehensive information, browse detailed profiles, and explore your hotel portfolio.
          </Typography>

        </Box>

         {/* Right Column - Campaign Images (3 layered images with brand radius) */}
         <Box sx={{ 
           flex: 1, // 50% width
           width: '100%',
           aspectRatio: '1', // Maintain square aspect ratio
           maxWidth: '100%', // Prevent horizontal overflow
           maxHeight: '100%', // Prevent vertical overflow
           alignSelf: { xs: 'center', lg: 'center' }, // Center vertically
           transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
           // Campaign-inspired layered design with brand guidelines
           position: 'relative',
         }}>
           
           {/* Layer 1 - Outer Image (0.5x radius) */}
           <Box sx={{
             position: 'absolute',
             top: 0,
             left: 0,
             right: 0,
             bottom: 0,
             borderRadius: 2, // 0.5x radius
             overflow: 'hidden',
             zIndex: 1,
             backgroundImage: 'url()',
             backgroundSize: 'cover',
             backgroundPosition: 'center',
             backgroundRepeat: 'no-repeat',
             // Monochromatic color overlay - all colors become shades of secondary color
             '&::before': {
               content: '""',
               position: 'absolute',
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               borderRadius: 2,
               background: theme => theme.palette.primary.main,
               mixBlendMode: 'hue',
               opacity: 1,
               zIndex: 1,
             }
           }} />
           
           {/* Layer 2 - Middle Image (1x radius) */}
           <Box sx={{
             position: 'absolute',
             top: 60, // Campaign-style large spacing
             left: 60, // Campaign-style large spacing
             right: 60,
             bottom: 60,
             borderRadius: 4, // 1x radius
             overflow: 'hidden',
             zIndex: 2,
             backgroundImage: 'url(https://nordicchoicehotels.papirfly.no/readimage.aspx/asset.jpg?pubid=0ZcDyRerwvqu2BFzdCoMTA&quality=9)',
             backgroundSize: 'cover',
             backgroundPosition: 'center',
             backgroundRepeat: 'no-repeat',
             // Monochromatic color overlay - all colors become shades of secondary color
             '&::before': {
               content: '""',
               position: 'absolute',
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               borderRadius: 4,
               background: theme => theme.palette.primary.main,
               mixBlendMode: 'hue',
               opacity: 1,
               zIndex: 1,
             }
           }} />
           
           {/* Layer 3 - Background Image for Search Card (2x radius) */}
           <Box sx={{
             position: 'absolute',
             top: 120, // Campaign-style very large spacing
             left: 120, // Campaign-style very large spacing
             right: 120,
             bottom: 120,
             borderRadius: 8, // 2x radius
             overflow: 'hidden',
             zIndex: 3,
             backgroundImage: 'url(https://nordicchoicehotels.papirfly.no/readimage.aspx/asset.jpg?pubid=JUdvbpjy2inpjg0Iheu1ig&quality=9)',
             backgroundSize: 'cover',
             backgroundPosition: 'center',
             backgroundRepeat: 'no-repeat',
             // Monochromatic color overlay - all colors become shades of secondary color
             '&::before': {
               content: '""',
               position: 'absolute',
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               borderRadius: 8,
               background: theme => theme.palette.primary.main,
               mixBlendMode: 'hue',
               opacity: 1,
               zIndex: 1,
             }
           }} />

           {/* Layer 4 - Search Card Overlay (2x radius) - Only show when sidebar is collapsed */}
           <Box sx={{
             position: 'absolute',
             top: 120, // Campaign-style very large spacing
             left: 120, // Campaign-style very large spacing
             right: 120,
             bottom: 120,
             borderRadius: 8, // 2x radius
             overflow: 'visible', // Allow search bar to show
             zIndex: 4,
             // Make it look more like a search interface
             background: theme => alpha(theme.palette.primary.main, 0.5),
             backdropFilter: 'blur(8px)',
             WebkitBackdropFilter: 'blur(8px)',
             border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
             display: 'flex',
             flexDirection: 'column',
             alignItems: 'center',
             justifyContent: 'center',
             p: 3,
             boxShadow: theme => `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
             // Fade animation based on sidebar state
             opacity: sidebarCollapsed ? 1 : 0,
             transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
             pointerEvents: sidebarCollapsed ? 'auto' : 'none',
           }}>
             {/* Search Title */}
             <Typography 
               variant="h4" 
               sx={{ 
                 fontFamily: 'StrawberrySansDisplay',
                 fontWeight: 500,
                 fontSize: '1.5rem',
                 color: 'text.primary',
                 mb: 2,
                 textAlign: 'center',
                 // Content animation
                 opacity: sidebarCollapsed ? 1 : 0,
                 transform: sidebarCollapsed ? 'translateY(0)' : 'translateY(20px)',
                 transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s', // Slight delay
               }}
             >
               Search Hotel
             </Typography>
             
             {/* Search Bar */}
             <Box sx={{
               width: '100%',
               maxWidth: '280px',
               zIndex: 20,
               // Content animation
               opacity: sidebarCollapsed ? 1 : 0,
               transform: sidebarCollapsed ? 'translateY(0)' : 'translateY(20px)',
               transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.2s', // Slightly more delay
             }}>
                 <Box sx={{ 
                   width: '100%',
                   height: '48px',
                   bgcolor: theme => alpha(theme.palette.background.default, 0.8),
                 borderRadius: 3,
                 overflow: 'hidden',
                 position: 'relative',
                 boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                 transition: 'all 0.3s ease',
                 '&:focus-within': {
                   boxShadow: theme => `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                 }
               }}>
                 <TextField
                   size="small"
                   value={searchQuery}
                   onChange={handleSearchChange}
                   onKeyDown={handleKeyDown}
                   placeholder={showTypingAnimation && searchQuery.length === 0 ? typingText : 'Search hotels...'}
                   inputProps={{
                     'data-campaign-search': true
                   }}
                   sx={{
                     flex: 1,
                     width: '100%',
                     '& .MuiOutlinedInput-root': {
                       height: '48px',
                       borderRadius: 3,
                       border: 'none',
                       bgcolor: 'transparent',
                       '& fieldset': {
                         border: 'none',
                       },
                       '&:hover fieldset': {
                         border: 'none',
                       },
                       '&.Mui-focused fieldset': {
                         border: 'none',
                       },
                       '&:hover': {
                         bgcolor: 'transparent',
                       },
                       '&.Mui-focused': {
                         bgcolor: 'transparent',
                       },
                     },
                     '& .MuiInputBase-input': {
                       height: '48px',
                       padding: '0 16px',
                       fontSize: '0.95rem',
                       fontFamily: 'StrawberrySansText',
                       '&::placeholder': {
                         opacity: 0.7,
                         color: 'text.secondary',
                       },
                     },
                   }}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start" sx={{ ml: 1 }}>
                         <SearchIcon sx={{ 
                           color: 'white', 
                           fontSize: '1.2rem',
                           opacity: 0.9 
                         }} />
                       </InputAdornment>
                     ),
                     endAdornment: searchQuery && (
                       <InputAdornment position="end">
                         <IconButton
                           onClick={() => handleSearchChange({ target: { value: '' } })}
                           size="small"
                           sx={{
                             color: 'white',
                             opacity: 0.8,
                             '&:hover': {
                               opacity: 1,
                               bgcolor: 'rgba(255, 255, 255, 0.1)'
                             },
                             transition: 'all 0.2s ease',
                           }}
                         >
                           <ClearIcon sx={{ fontSize: '1.1rem' }} />
                         </IconButton>
                       </InputAdornment>
                     ),
                   }}
                 />
               </Box>
               
               {/* Search Results Dropdown */}
               {showSearchResults && searchResults.length > 0 && (
                 <Paper
                   elevation={8}
                   sx={{
                     position: 'absolute',
                     top: '100%',
                     left: 0,
                     right: 0,
                     mt: 1,
                     maxHeight: '300px',
                     overflowY: 'auto',
                     zIndex: 1000,
                     bgcolor: theme => alpha(theme.palette.background.default, 0.8),
                     backdropFilter: 'blur(10px)',
                     WebkitBackdropFilter: 'blur(10px)',
                     border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                     borderRadius: 3,
                     boxShadow: theme => `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
                   }}
                 >
                   <List dense sx={{ py: 1, px: 1 }}>
                     {searchResults.map((hotel, index) => (
                       <ListItem
                         key={hotel.id}
                         ref={index === selectedResultIndex ? selectedItemRef : null}
                         button
                         onClick={() => handleHotelSelect(hotel)}
                         sx={{
                           px: 2,
                           py: 1,
                           borderRadius: 2,
                           cursor: 'pointer',
                           bgcolor: index === selectedResultIndex 
                             ? theme => alpha(theme.palette.primary.main, 0.12)
                             : 'transparent',
                           '&:hover': {
                             bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                           },
                           transition: 'background-color 0.2s ease',
                         }}
                       >
                         <ListItemAvatar>
                           <Avatar
                             src={getChainLogoUrl?.(hotel.categoryData?.category_chain?.value)}
                             alt={hotel.categoryData?.category_chain?.value || hotel.name || hotel.categoryData?.category_name?.value}
                             sx={{
                               width: 40,
                               height: 40,
                               bgcolor: 'transparent',
                               borderRadius: 1,
                               objectFit: 'contain',
                             }}
                           >
                             {(hotel.categoryData?.category_chain?.value || hotel.name || hotel.categoryData?.category_name?.value)?.charAt(0)?.toUpperCase()}
                           </Avatar>
                         </ListItemAvatar>
                         <ListItemText
                           primary={
                             <Typography
                               variant="body2"
                               sx={{
                                 fontFamily: 'StrawberrySansText',
                                 fontWeight: 500,
                                 fontSize: '0.9rem',
                                 color: 'text.primary',
                                 lineHeight: 1.3,
                               }}
                             >
                               {hotel.name || hotel.categoryData?.category_name?.value || hotel.categoryData?.category_hotelname?.value}
                             </Typography>
                           }
                             secondary={
                               <Typography
                                 variant="caption"
                                 sx={{
                                   fontFamily: 'StrawberrySansText',
                                   fontSize: '0.75rem',
                                   color: 'text.secondary',
                                   lineHeight: 1.2,
                                 }}
                               >
                                 {hotel.id}
                               </Typography>
                             }
                         />
                       </ListItem>
                     ))}
                   </List>
                 </Paper>
               )}
             </Box>
             
           </Box>
         </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default NoHotelSelectedCampaign;
